import { Alerts } from "../../../utils/Alerts.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { SelectMultiOptionWrapper } from "../../../components/Select/SelectMultiOptionWrapper.js";
import { useStateDebounced } from "../../../hooks/useStateDebounced.js";
import { ALL_LOCATIONS_ID } from "../constants.js";
import { useRegionContext } from "../../Regions/contexts/RegionContext.js";
import { useLocations } from "../hooks/useLocations.js";
import { createMockReactSyntheticEvent } from "../../../utils/mock-react-synthetic-event.js";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Icon, Image, Loading, Select } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import { castArray, uniqBy } from "lodash-es";
import { MdLocationOff } from "react-icons/md";
//#region src/modules/Locations/shared/SelectLocation.tsx
var ADDITIONAL_OPTIONS = [{ id: ALL_LOCATIONS_ID }];
var Base = ({ location }) => {
	if (!location) return null;
	if (location.id === "loc-") return /* @__PURE__ */ jsx("div", { children: "All Locations" });
	const { address, name, primaryImageUrl } = location;
	return /* @__PURE__ */ jsxs("div", {
		className: "gap-x-sm flex items-center",
		children: [/* @__PURE__ */ jsx("div", {
			className: "shrink-0",
			children: /* @__PURE__ */ jsx(Image, {
				src: primaryImageUrl,
				className: "max-h-xl"
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "truncate",
			children: [/* @__PURE__ */ jsx("div", {
				className: "truncate text-xs",
				children: name
			}), /* @__PURE__ */ jsxs("div", {
				className: "text-color-subtle truncate text-xs",
				children: [address?.streetAddress && /* @__PURE__ */ jsxs("span", {
					className: "truncate font-medium",
					children: [
						address?.streetAddress,
						",",
						" "
					]
				}), address?.locality]
			})]
		})]
	});
};
var SelectLocationRenderComponents = {
	ListItem: (locationOrSuggestion) => {
		if (locationOrSuggestion.placeId) return locationOrSuggestion.description;
		return /* @__PURE__ */ jsx(Base, { location: locationOrSuggestion });
	},
	SelectedMulti: (location, onRemove) => /* @__PURE__ */ jsx(SelectMultiOptionWrapper, {
		onRemove,
		children: /* @__PURE__ */ jsx("div", {
			className: "ml-xxs mr-xs max-w-full",
			children: /* @__PURE__ */ jsx(Base, { location })
		})
	}),
	SelectedSingle: (location) => /* @__PURE__ */ jsx(Base, { location })
};
var reducer = (state, { key, options, type }) => {
	if (type === "additional") {
		const _options = [...state.options, ...options];
		return {
			key,
			options: _options,
			total: _options.length
		};
	} else if (type === "reset") return {
		key,
		options,
		total: options.length
	};
};
var SelectLocation = ({ active = true, allowAddresses, allowAllOption, createAddressRegionId, idFilter, isSearchable, limit = 25, multi, onChange, onChangeOptions, onChangeValue, onLocationValue, regionIds, value, ...atalayaSelectProps }) => {
	const [, debouncedSearchText, setSearchText] = useStateDebounced("");
	const [addressLoading, setAddressLoading] = useState(false);
	const [showAddressSearch, setShowAddressSearch] = useState(false);
	const [selectedLocations, setSelectedLocations] = useState([]);
	const [response, dispatch] = useReducer(reducer, {
		key: "",
		options: [],
		total: 0
	});
	const [skip, setSkip] = useState(0);
	const { rpcService } = useRPCContext();
	const regionCtx = useRegionContext();
	const _regionIds = regionIds || !!regionCtx?.regionId && [regionCtx.regionId] || void 0;
	const query = useMemo(() => ({
		active,
		idFilter,
		includeAddresses: allowAddresses,
		pagination: {
			limit,
			skip
		},
		regionIds: _regionIds,
		searchText: debouncedSearchText
	}), [
		active,
		debouncedSearchText,
		idFilter,
		limit,
		regionCtx,
		regionIds,
		skip
	]);
	const { data, loading } = useLocations(query);
	useEffect(() => {
		if (!data || response?.key === JSON.stringify(query)) return;
		if (Array.isArray(data?.locations)) dispatch({
			key: JSON.stringify(query),
			options: data.locations,
			type: skip ? "additional" : "reset"
		});
	}, [data]);
	useEffect(() => {
		if (!value) {
			onLocationValue?.([]);
			setSelectedLocations([]);
			return;
		}
		const _value = castArray(value);
		const existingLocations = [];
		const idsToFetch = [];
		_value.forEach((selectedId) => {
			const foundLocation = (allowAllOption ? [...ADDITIONAL_OPTIONS, ...response?.options || []] : response?.options).find((location) => location.id === selectedId);
			if (foundLocation) existingLocations.push(foundLocation);
			else idsToFetch.push(selectedId);
		});
		onLocationValue?.(existingLocations);
		setSelectedLocations(existingLocations);
		if (idsToFetch.length) rpcService.Locations.GetLocationsList.v1.call({ idFilter: castArray(value) }).then((_response) => {
			const newSelectedLocations = uniqBy([...existingLocations, ..._response.data.locations], "id");
			onLocationValue?.(newSelectedLocations);
			setSelectedLocations(newSelectedLocations);
		});
	}, [value]);
	useEffect(() => {
		if (showAddressSearch && debouncedSearchText) {
			setAddressLoading(true);
			rpcService.Geo.GetGooglePlacesAutocompleteSuggestions.v1.call({ text: debouncedSearchText }).then(({ data }) => {
				dispatch({
					key: `address:${debouncedSearchText}`,
					options: data.suggestions,
					type: "reset"
				});
			}).finally(() => setAddressLoading(false));
		}
	}, [showAddressSearch, debouncedSearchText]);
	const handleLoadOptions = useCallback(({ additional, text } = {}) => {
		if (text !== debouncedSearchText) {
			setSearchText(text || "");
			setSkip(0);
			return;
		}
		if (loading) return;
		if (additional && data?.total && data.total > response?.total) setSkip(skip + limit);
	}, [
		data?.total,
		debouncedSearchText,
		limit,
		loading,
		response?.total,
		skip
	]);
	const handleOnChange = async (option) => {
		if (option?.placeId) {
			setAddressLoading(true);
			try {
				const { data: googlePlace } = await rpcService.Geo.GetGooglePlaceDetails.v1.call({ placeId: option.placeId });
				const { data: { locationId: newLocationId } } = await rpcService.Locations.CreateAddressLocation.v1.call({ location: {
					address: googlePlace.details.address,
					geo: { coordinates: googlePlace.details.geo },
					name: googlePlace.details.name,
					regionId: createAddressRegionId
				} });
				const { data: { locations: [location] } } = await rpcService.Locations.GetLocationsList.v1.call({ idFilter: [newLocationId] });
				option = location;
				if (!option?.id) throw new Error("address Location was not created");
				else setSelectedLocations([location]);
			} catch (e) {
				console.log("error creating address Location in <LocationSelect />", option, e);
				Alerts.error("There was a problem creating the address Location", e.message);
				option = void 0;
			}
			setAddressLoading(false);
		}
		let value;
		if (Array.isArray(option)) value = option.map((opt) => opt.id);
		else if (option) value = option.id;
		else if (multi) value = [];
		else value = null;
		if (onChangeValue) onChangeValue(value);
		if (onChangeOptions) onChangeOptions(option);
		if (onChange) onChange(createMockReactSyntheticEvent({ value }));
	};
	const NoOptions = () => /* @__PURE__ */ jsxs("div", {
		className: "gap-y-xs mt-sm flex flex-col items-center",
		children: [
			/* @__PURE__ */ jsx(Icon, {
				Icon: MdLocationOff,
				color: "subtle",
				size: "xxl"
			}),
			/* @__PURE__ */ jsx("span", {
				className: "text-color-subtle",
				children: "No Locations"
			}),
			/* @__PURE__ */ jsx(Button, {
				onClick: () => setShowAddressSearch(true),
				variant: "main",
				subtle: true,
				children: /* @__PURE__ */ jsx("span", {
					className: "ml-xxs",
					children: "Search Google Places"
				})
			})
		]
	});
	return /* @__PURE__ */ jsx(Select, {
		onBlur: () => {
			if (showAddressSearch) setShowAddressSearch(false);
		},
		RenderListItem: SelectLocationRenderComponents.ListItem,
		RenderSelectedListItem: SelectLocationRenderComponents.ListItem,
		RenderSelectedMulti: SelectLocationRenderComponents.SelectedMulti,
		RenderSelectedSingle: SelectLocationRenderComponents.SelectedSingle,
		RenderNoOptions: allowAddresses && !showAddressSearch ? NoOptions : void 0,
		isLoading: loading || addressLoading,
		isSearchable: isSearchable || allowAddresses,
		loadOptions: handleLoadOptions,
		multi: allowAddresses ? false : multi,
		optionLabelExtractor: ({ name }) => name,
		optionValueExtractor: ({ id }) => id,
		options: response?.options,
		value: selectedLocations,
		filterOption: null,
		onChangeOptions: handleOnChange,
		subText: addressLoading ? /* @__PURE__ */ jsx(Loading, {
			message: "Loading address",
			messagePosition: "horizontal"
		}) : atalayaSelectProps.subText,
		...atalayaSelectProps,
		allowCreate: false
	});
};
//#endregion
export { SelectLocation, SelectLocationRenderComponents };

//# sourceMappingURL=SelectLocation.js.map